import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { colors, API, fmt } from '../utils/theme';
import {
  PageHeader, StatCard, Badge, LoadingSpinner,
} from '../components/UI';
import { ChevronLeft, ChevronRight, Landmark, CreditCard, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

const mesAtual = () => new Date().toISOString().slice(0, 7);

export default function ConciliacaoCCPage() {
  const [mes, setMes] = useState(mesAtual());
  const [resumo, setResumo] = useState(null);
  const [transacoes, setTransacoes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');

  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (mes) params.set('mes_referencia', mes);
    if (filtroTipo) params.set('tipo', filtroTipo);
    if (filtroStatus) params.set('status', filtroStatus);
    if (busca) params.set('busca', busca);

    Promise.all([
      fetch(`${API}/conciliacao/conta-corrente/resumo?mes_referencia=${mes}`).then(r => r.json()),
      fetch(`${API}/conciliacao/conta-corrente/transacoes?${params}`).then(r => r.json()),
      fetch(`${API}/conciliacao/conta-corrente/categorias?mes_referencia=${mes}`).then(r => r.json()),
    ]).then(([r, t, c]) => {
      setResumo(r);
      setTransacoes(Array.isArray(t) ? t : []);
      setCategorias(Array.isArray(c) ? c : []);
      setLoading(false);
    }).catch(e => { console.error(e); setLoading(false); });
  }, [mes, filtroTipo, filtroStatus, busca]);

  useEffect(() => { loadData(); }, [loadData]);

  const shiftMes = (delta) => {
    const [y, m] = mes.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMes(d.toISOString().slice(0, 7));
  };

  const btnStyle = (active) => ({
    padding: '6px 12px', borderRadius: 8, border: `1px solid ${active ? colors.primary : colors.border}`,
    background: active ? `${colors.primary}22` : 'transparent', color: active ? colors.primary : colors.textMuted,
    cursor: 'pointer', fontSize: 13, fontWeight: 500,
  });

  const debitos = categorias.filter(c => c.tipo === 'DEBITO');
  const creditos = categorias.filter(c => c.tipo === 'CREDITO');

  return (
    <div>
      <PageHeader
        title="Conciliacao Conta Corrente"
        subtitle="Lancamentos financeiros XMiles — futura reconciliacao com extrato bancario (API Inter)"
      />

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Debitos (saidas)" value={resumo?.total_debitos ?? '-'} color={colors.danger}
          subtitle={resumo ? fmt(resumo.valor_debitos) : ''} />
        <StatCard label="Creditos (entradas)" value={resumo?.total_creditos ?? '-'} color={colors.success}
          subtitle={resumo ? fmt(resumo.valor_creditos) : ''} />
        <StatCard label="Conciliados" value={resumo?.conciliados ?? '-'} color={colors.info} />
        <StatCard label="Pendentes" value={resumo?.pendentes ?? '-'} color={colors.warning} />
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20,
        background: colors.card, padding: '12px 16px', borderRadius: 12, border: `1px solid ${colors.border}`,
        flexWrap: 'wrap',
      }}>
        <button onClick={() => shiftMes(-1)} style={btnStyle(false)}><ChevronLeft size={16} /></button>
        <input type="month" value={mes} onChange={e => setMes(e.target.value)}
          style={{
            padding: '6px 12px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bg, color: colors.text, fontSize: 14, colorScheme: 'dark',
          }} />
        <button onClick={() => shiftMes(1)} style={btnStyle(false)}><ChevronRight size={16} /></button>
        <button onClick={() => setMes(mesAtual())} style={btnStyle(mes === mesAtual())}>Mes Atual</button>

        <div style={{ width: 1, height: 24, background: colors.border, margin: '0 4px' }} />

        <button onClick={() => setFiltroTipo('')} style={btnStyle(!filtroTipo)}>Todos</button>
        <button onClick={() => setFiltroTipo('DEBITO')} style={{
          ...btnStyle(filtroTipo === 'DEBITO'),
          borderColor: filtroTipo === 'DEBITO' ? colors.danger : colors.border,
          color: filtroTipo === 'DEBITO' ? colors.danger : colors.textMuted,
          background: filtroTipo === 'DEBITO' ? `${colors.danger}22` : 'transparent',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <ArrowDownCircle size={14} /> Debitos
          </span>
        </button>
        <button onClick={() => setFiltroTipo('CREDITO')} style={{
          ...btnStyle(filtroTipo === 'CREDITO'),
          borderColor: filtroTipo === 'CREDITO' ? colors.success : colors.border,
          color: filtroTipo === 'CREDITO' ? colors.success : colors.textMuted,
          background: filtroTipo === 'CREDITO' ? `${colors.success}22` : 'transparent',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <ArrowUpCircle size={14} /> Creditos
          </span>
        </button>

        <div style={{ width: 1, height: 24, background: colors.border, margin: '0 4px' }} />

        <button onClick={() => setFiltroStatus('')} style={btnStyle(!filtroStatus)}>Todos</button>
        <button onClick={() => setFiltroStatus('pendente')} style={btnStyle(filtroStatus === 'pendente')}>Pendentes</button>
        <button onClick={() => setFiltroStatus('conciliado')} style={btnStyle(filtroStatus === 'conciliado')}>Conciliados</button>

        <div style={{ flex: 1 }} />

        <input
          value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar..."
          style={{
            padding: '6px 12px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bg, color: colors.text, fontSize: 13, width: 180, outline: 'none',
          }}
        />
      </div>

      {loading ? <LoadingSpinner /> : (
        <>
          {/* Categorias breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            {/* Debitos */}
            <div style={{
              background: colors.card, borderRadius: 12, border: `1px solid ${colors.danger}33`,
              padding: 16,
            }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: colors.danger, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <ArrowDownCircle size={16} /> Debitos por Categoria
              </h3>
              {debitos.length === 0 ? (
                <div style={{ color: colors.textDim, fontSize: 13, padding: 12 }}>Nenhum debito</div>
              ) : debitos.map((c, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', borderBottom: i < debitos.length - 1 ? `1px solid ${colors.border}22` : 'none',
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{c.grupo || 'Outros'}</div>
                    <div style={{ fontSize: 11, color: colors.textDim }}>{c.qtd} lancamentos</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: colors.danger }}>{fmt(c.total)}</div>
                </div>
              ))}
            </div>

            {/* Creditos */}
            <div style={{
              background: colors.card, borderRadius: 12, border: `1px solid ${colors.success}33`,
              padding: 16,
            }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: colors.success, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <ArrowUpCircle size={16} /> Creditos por Categoria
              </h3>
              {creditos.length === 0 ? (
                <div style={{ color: colors.textDim, fontSize: 13, padding: 12 }}>Nenhum credito</div>
              ) : creditos.map((c, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', borderBottom: i < creditos.length - 1 ? `1px solid ${colors.border}22` : 'none',
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{c.grupo || 'Outros'}</div>
                    <div style={{ fontSize: 11, color: colors.textDim }}>{c.qtd} lancamentos</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: colors.success }}>{fmt(c.total)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Transaction list */}
          <div style={{
            background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`,
          }}>
            <div style={{
              padding: '12px 16px', borderBottom: `1px solid ${colors.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.text, margin: 0 }}>
                Lancamentos ({transacoes.length})
              </h3>
              <div style={{ fontSize: 12, color: colors.textMuted }}>
                Futura reconciliacao com API Inter
              </div>
            </div>

            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              {transacoes.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: colors.textDim, fontSize: 14 }}>
                  Nenhum lancamento encontrado
                </div>
              ) : transacoes.map(t => (
                <div key={t.id} style={{
                  padding: '10px 16px', borderBottom: `1px solid ${colors.border}11`,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  {/* Icon */}
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: t.tipo === 'DEBITO' ? `${colors.danger}22` : `${colors.success}22`,
                    color: t.tipo === 'DEBITO' ? colors.danger : colors.success,
                    flexShrink: 0,
                  }}>
                    {t.tipo === 'DEBITO' ? <ArrowDownCircle size={16} /> : <ArrowUpCircle size={16} />}
                  </div>

                  {/* Description */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: colors.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.descricao}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span>{t.data_compra || t.data_vencimento || '-'}</span>
                      <span>|</span>
                      <span>{t.nome_cartao || '-'}</span>
                      {t.parcela && <span>| {t.parcela}</span>}
                      {t.categoria && (
                        <Badge text={t.categoria} color={colors.info} />
                      )}
                    </div>
                  </div>

                  {/* Value */}
                  <div style={{
                    fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap',
                    color: t.tipo === 'DEBITO' ? colors.danger : colors.success,
                  }}>
                    {t.tipo === 'DEBITO' ? '-' : '+'}{fmt(Math.abs(t.valor))}
                  </div>

                  {/* Status */}
                  <Badge
                    text={t.conciliado ? 'Conciliado' : 'Pendente'}
                    color={t.conciliado ? colors.success : colors.warning}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
